import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiChevronDown } from "react-icons/fi";
import { shakeModal } from "../../../utils/modalAttention";
import s from "./OknoBramki.module.css";

/**
 * ════════════════════════════════════════════════════════════════════════════
 * OknoBramki — WSPÓLNE okno-bramka aplikacji.
 *
 * „Bramka" to okno, którego nie da się pominąć: NIE MA ✕, NIE MA onClose, a jedyne
 * wyjście prowadzi przez przycisk akceptacji. To jest ZAMIERZONE i nie wolno tego
 * zmieniać — regulaminu i komunikatu RODO nie zamyka się „na krzyżyk".
 * Klik w tło nie zamyka, ale MUSI dać odpowiedź: potrząśnięcie (shakeModal), żeby
 * użytkownik nie klikał w pustkę i nie zgadywał, czy aplikacja żyje.
 *
 * POWSTAŁO Z DWÓCH KOPII. Regulamin (TermsOfServiceModal) i komunikat
 * (AnnouncementModal) były bliźniakami z kopiuj-wklej: 385 z 403 linii arkusza
 * i 157 z 225 linii JS identycznych. Każdą poprawkę trzeba było robić dwa razy,
 * a różnice między nimi — inne przyciemnienie tła, inne rozmycie, inna warstwa
 * z-index, inna szerokość — były widoczne dla użytkownika i niczym nieuzasadnione.
 *
 * KOLOR WCHODZI SŁOWNIKIEM CSS, NIE Z JS. Wcześniej komunikat trzymał cztery
 * gradienty, cztery kolory i cztery tła w literale JS, a regulamin miał zaszytą
 * zieleń, której nie ma nigdzie w palecie aplikacji. Tutaj komponent podaje wyłącznie
 * NAZWĘ TONU w atrybucie `data-ton`, a barwę dobiera arkusz z tokenów motywu.
 *
 * Propsy:
 *   ton            — "info" | "ostrzezenie" | "krytyczny" | "rodo" | "regulamin"
 *   ikona          — komponent ikony (react-icons) do głowy okna
 *   odznaka        — krótka etykieta nad tytułem (wielkimi literami)
 *   tytul          — tytuł okna
 *   meta           — [{ ikona, tekst }] wiersz autora/daty (pomijany, gdy pusty)
 *   postep         — 0..100 albo null; null = brak paska postępu czytania
 *   onScrollTresci — handler przewijania treści (bramka „przewiń do końca")
 *   refTresci      — ref na przewijany kontener (pomiar scrollHeight)
 *   podpowiedz     — tekst podpowiedzi na dole treści albo null
 *   stopkaInfo     — { ikona, tekst } informacja po lewej w stopce albo null
 *   akcja          — { etykieta, ikona, onClick, zablokowana, ladowanie }
 *   poziom         — "modal" (domyślnie) | "nad" (warstwa ponad innymi bramkami)
 * ════════════════════════════════════════════════════════════════════════════
 */

/** Tony rozpoznawane przez słownik w OknoBramki.module.css. */
export const TONY_BRAMKI = ["info", "ostrzezenie", "krytyczny", "rodo", "regulamin"];

const wariantyTla = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.25, ease: "easeOut" } },
    exit: { opacity: 0, transition: { duration: 0.2 } },
};

const wariantyPanelu = {
    hidden: { opacity: 0, scale: 0.96, y: 24 },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1] },
    },
    exit: { opacity: 0, scale: 0.97, y: 16, transition: { duration: 0.18 } },
};

/**
 * Treść redakcyjna (HTML) w kanonicznej typografii okna.
 * SANITYZACJĘ ROBI STRONA WYWOŁUJĄCA — regulamin i komunikat mają różne, świadomie
 * dobrane reguły (regulamin dopuszcza zdalne obrazy, komunikat nie), więc decyzja
 * bezpieczeństwa zostaje w ich plikach, a nie we wspólnym opakowaniu.
 */
export const TrescHtml = ({ html }) => (
    <div className={s.trescHtml} dangerouslySetInnerHTML={{ __html: html }} />
);

const OknoBramki = ({
    ton = "info",
    ikona: Ikona = null,
    odznaka = null,
    tytul = "",
    meta = [],
    postep = null,
    onScrollTresci,
    refTresci,
    podpowiedz = null,
    stopkaInfo = null,
    akcja,
    poziom = "modal",
    /* [polecenie właściciela 2026-09-20] JEDYNY wyjątek od „bramka nie ma ✕": administrator w trybie
       „Zaloguj jako” (podgląd konta) ma móc zamknąć okno NA CZAS OGLĄDANIA — tak jak okno rejestracji.
       Zamknięcie jest lokalne i nic nie odhacza; pracownika przy jego logowaniu okno dotyczy tak samo.
       Poza podglądem prop jest `null` i przycisk nie istnieje — bramka zostaje bramką. */
    onZamknijWPodgladzie = null,
    children,
}) => {
    const IkonaStopki = stopkaInfo?.ikona || null;
    const IkonaAkcji = akcja?.ikona || null;
    const wiersze = (meta || []).filter((w) => w && w.tekst);

    return createPortal(
        <AnimatePresence>
            {/* Klik w tło NIE zamyka bramki — daje potrząśnięcie panelem (warunek W-10). */}
            <motion.div
                className={`${s.tlo} ${poziom === "nad" ? s.tloNad : ""}`}
                onClick={shakeModal}
                variants={wariantyTla}
                initial="hidden"
                animate="visible"
                exit="exit"
            >
                <motion.div
                    className={s.panel}
                    data-ton={ton}
                    role="dialog"
                    aria-modal="true"
                    aria-label={tytul}
                    variants={wariantyPanelu}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={s.naglowek}>
                        {onZamknijWPodgladzie && (
                            <button
                                type="button"
                                className={s.zamknijPodglad}
                                onClick={onZamknijWPodgladzie}
                                data-testid="zamknij-okno-w-podgladzie"
                                title="Zamknij na czas oglądania. Pracownika to NIE zwalnia — przy jego logowaniu okno pojawi się tak samo."
                                aria-label="Zamknij to okno na czas oglądania konta"
                            >
                                ✕
                            </button>
                        )}
                        {Ikona && (
                            <div className={s.naglowekIkona} aria-hidden="true">
                                <Ikona />
                            </div>
                        )}
                        <div className={s.naglowekTeksty}>
                            {odznaka && <span className={s.odznaka}>{odznaka}</span>}
                            <h2 className={s.tytul}>{tytul}</h2>
                        </div>
                    </div>

                    {wiersze.length > 0 && (
                        <div className={s.meta}>
                            {wiersze.map((wiersz, i) => {
                                const IkonaMeta = wiersz.ikona;
                                return (
                                    <div className={s.metaItem} key={i}>
                                        {IkonaMeta && <IkonaMeta />}
                                        <span>{wiersz.tekst}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {postep !== null && postep !== undefined && (
                        <div
                            className={s.pasekPostepu}
                            role="progressbar"
                            aria-valuenow={Math.round(postep)}
                            aria-valuemin={0}
                            aria-valuemax={100}
                        >
                            <motion.div
                                className={s.wypelnieniePostepu}
                                initial={{ width: 0 }}
                                animate={{ width: `${postep}%` }}
                                transition={{ duration: 0.2 }}
                            />
                        </div>
                    )}

                    <div
                        className={s.trescOkna}
                        onScroll={onScrollTresci}
                        ref={refTresci}
                    >
                        {children}

                        {podpowiedz && (
                            <motion.div
                                className={s.podpowiedz}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 }}
                            >
                                <FiChevronDown className={s.podpowiedzIkona} />
                                <span>{podpowiedz}</span>
                            </motion.div>
                        )}
                    </div>

                    <div className={s.stopka}>
                        {stopkaInfo && (
                            <div className={s.stopkaInfo}>
                                {IkonaStopki && <IkonaStopki />}
                                <span>{stopkaInfo.tekst}</span>
                            </div>
                        )}
                        {akcja && (
                            <button
                                type="button"
                                className={s.przyciskGlowny}
                                onClick={akcja.onClick}
                                disabled={!!akcja.zablokowana || !!akcja.ladowanie}
                            >
                                {akcja.ladowanie ? (
                                    <span className={s.spinner} aria-hidden="true" />
                                ) : (
                                    IkonaAkcji && <IkonaAkcji />
                                )}
                                <span>{akcja.etykieta}</span>
                            </button>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>,
        document.body
    );
};

export default OknoBramki;
